/**
 * WASSER E-commerce Platform - Shared ProtectedRoute Component
 *
 * Унифицированный компонент для защиты маршрутов.
 * Используется в Client и Admin приложениях.
 *
 * @example
 * // В Client приложении
 * <ProtectedRoute requireAuth requireB2B>
 *   <B2BDashboard />
 * </ProtectedRoute>
 *
 * // В Admin приложении
 * <ProtectedRoute requireAuth requireAdmin>
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRouteProtection } from '../../hooks/useRouteProtection.js';

/**
 * Компонент загрузки по умолчанию
 */
const DefaultLoadingComponent = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      gap: 2,
    }}
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary">
      Проверка доступа...
    </Typography>
  </Box>
);

/**
 * ProtectedRoute Component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Защищенный контент
 * @param {Object} props.authState - Состояние авторизации из контекста
 * @param {boolean} [props.requireAuth=true] - Требуется авторизация
 * @param {boolean} [props.requireB2B=false] - Требуется B2B доступ
 * @param {boolean} [props.requireAdmin=false] - Требуется admin роль
 * @param {string} [props.loginPath='/login'] - Путь для редиректа неавторизованных
 * @param {string} [props.b2bApplyPath='/b2b/apply'] - Путь для B2B заявки
 * @param {string} [props.defaultPath='/'] - Путь по умолчанию
 * @param {React.ComponentType} [props.LoadingComponent] - Компонент загрузки
 * @param {boolean} [props.saveRedirectPath=true] - Сохранять путь для редиректа после логина
 */
const ProtectedRoute = ({
  children,
  authState,
  requireAuth = true,
  requireB2B = false,
  requireAdmin = false,
  loginPath = '/login',
  b2bApplyPath = '/b2b/apply',
  defaultPath = '/',
  LoadingComponent = DefaultLoadingComponent,
  saveRedirectPath = true,
}) => {
  const location = useLocation();

  const { isAllowed, isLoading, redirectPath, reason } = useRouteProtection(
    authState,
    {
      requireAuth,
      requireB2B,
      requireAdmin,
      loginPath,
      b2bApplyPath,
      defaultPath,
    }
  );

  // Показываем загрузку
  if (isLoading) {
    return <LoadingComponent />;
  }

  // Если доступ запрещен - редиректим
  if (!isAllowed && redirectPath) {
    // Для логина сохраняем путь, куда пользователь хотел попасть
    if (reason === 'not_authenticated' && saveRedirectPath) {
      return (
        <Navigate
          to={redirectPath}
          state={{ from: location.pathname + location.search }}
          replace
        />
      );
    }

    return <Navigate to={redirectPath} replace />;
  }

  // Доступ разрешен - рендерим children
  return children;
};

export default ProtectedRoute;
